import { zodResolver } from "@hookform/resolvers/zod";
import { User } from "lucide-react";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import useApiRequest, { ApiResponse } from "@/lib/hooks/use-request";
import {
  CreatePersonRequestDto,
  OrganizationPreviewResponseDto,
  PersonDetailsResponseDto,
  UpdatePersonRequestDto,
} from "@/lib/interfaces/persons";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import OrganisationLinked from "../organisations-linked";

const PersonEditor = ({
  item,
  onSubmitDone,
  withoutRedirect,
  belongsToPipelineId,
}: {
  item?: PersonDetailsResponseDto;
  onSubmitDone?: (id: string, title: string) => void;
  withoutRedirect?: boolean;
  belongsToPipelineId?: string;
}) => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  const personSchema = z.object({
    firstName: z
      .string()
      .min(1, { message: "Vorname ist erforderlich" })
      .max(100, { message: "Vorname muss weniger als 100 Zeichen lang sein" }),
    lastName: z.string().max(100, { message: "Nachname muss weniger als 100 Zeichen lang sein" }).optional(),
    mail: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein").optional().or(z.literal("")),
    phone: z
      .string()
      .regex(/^[\d\s+()-]*$/, "Bitte geben Sie eine gültige Telefonnummer ein")
      .optional()
      .or(z.literal("")),
    postalCode: z
      .string()
      .max(20, { message: "Postleitzahl muss weniger als 20 Zeichen lang sein" })
      .optional()
      .or(z.literal("")),
    city: z.string().max(100, { message: "Stadt muss weniger als 100 Zeichen lang sein" }).optional().or(z.literal("")),
    street: z
      .string()
      .max(200, { message: "Straße muss weniger als 200 Zeichen lang sein" })
      .optional()
      .or(z.literal("")),
    country: z
      .string()
      .max(100, { message: "Land muss weniger als 100 Zeichen lang sein" })
      .optional()
      .or(z.literal("")),
    organization: z.object({
      id: z.string().optional(),
      title: z.string().optional(),
    }),
  });

  type PersonFormSchema = z.infer<typeof personSchema>;

  const form = useForm<PersonFormSchema>({
    resolver: zodResolver(personSchema),
    defaultValues: {
      firstName: item?.firstName || "",
      lastName: item?.lastName || "",
      mail: item?.mail || "",
      phone: item?.phone || "",
      postalCode: item?.postalCode || "",
      city: item?.city || "",
      street: item?.street || "",
      country: item?.country || "",
      organization: {
        id: item?.organizationPreview?._id || "",
        title: item?.organizationPreview?.name || "",
      },
    },
  });

  const onSubmit = async (data: PersonFormSchema) => {
    let res: ApiResponse<PersonDetailsResponseDto>;
    if (item) {
      // Update existing person
      const req: UpdatePersonRequestDto = {
        firstName: data.firstName !== item.firstName ? data.firstName : undefined,
        lastName: data.lastName !== item.lastName ? data.lastName : undefined,
        mail: data.mail !== item.mail ? data.mail : undefined,
        phone: data.phone !== item.phone ? data.phone : undefined,
        postalCode: data.postalCode !== item.postalCode ? data.postalCode : undefined,
        city: data.city !== item.city ? data.city : undefined,
        street: data.street !== item.street ? data.street : undefined,
        country: data.country !== item.country ? data.country : undefined,
        belongsToOrganizationId:
          data.organization.id !== item.organizationPreview?._id ? data.organization.id : undefined,
      };

      res = await apiRequest<PersonDetailsResponseDto>(`contacts/persons/${item._id}`, "PATCH", {
        body: req,
        toast: { toastText: "Person erfolgreich aktualisiert" },
      });
    } else {
      // Create new person
      const req: CreatePersonRequestDto = {
        firstName: data.firstName,
        ...(data.lastName && { lastName: data.lastName }),
        ...(data.mail && { mail: data.mail }),
        ...(data.phone && { phone: data.phone }),
        ...(data.postalCode && { postalCode: data.postalCode }),
        ...(data.city && { city: data.city }),
        ...(data.street && { street: data.street }),
        ...(data.country && { country: data.country }),
        ...(data.organization.id && { belongsToOrganizationId: data.organization.id }),
        ...(belongsToPipelineId && { belongsToPipelineIds: [belongsToPipelineId] }),
      };

      res = await apiRequest("contacts/persons", "POST", {
        body: req,
        toast: { toastText: "Person erfolgreich erstellt" },
      });
    }

    if (res.data) {
      if (onSubmitDone) {
        onSubmitDone(res.data._id, res.data.fullName);
      }
      if (!withoutRedirect) {
        navigate(`/contacts/persons/${res.data._id}`);
      }
    }
  };

  const onRemove = async (field: ControllerRenderProps<any, any>) => {
    if (item) {
      await apiRequest(`contacts/persons/${item._id}/unset-organization`, "PATCH");
      field.onChange(undefined);
    } else {
      field.onChange(undefined);
    }
  };

  const onAssign = async (itemPreview: { id: string; title: string }, field: ControllerRenderProps<any, any>) => {
    if (item) {
      await apiRequest(`contacts/persons/${item._id}`, "PATCH", {
        body: {
          belongsToOrganizationId: itemPreview.id,
        },
      });
    } else {
      field.onChange(itemPreview);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Vorname:*</FormLabel>
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Vorname" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Nachname:</FormLabel>
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Nachname" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">E-Mail:</FormLabel>
                <FormField
                  control={form.control}
                  name="mail"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} type="email" placeholder="Email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Telefon:</FormLabel>
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Telefon" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Address Information */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Straße:</FormLabel>
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Straße" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Postleitzahl:</FormLabel>
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Postleitzahl" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Stadt:</FormLabel>
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Stadt" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Land:</FormLabel>
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input {...field} placeholder="Land" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            {!withoutRedirect && !item && (
              <div className="flex flex-col gap-2">
                <FormLabel className="font-medium">Unternehmen:</FormLabel>
                <FormField
                  control={form.control}
                  name="organization"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <OrganisationLinked
                          item={
                            field.value?.id
                              ? ({
                                  _id: field.value.id,
                                  name: field.value.title,
                                } as OrganizationPreviewResponseDto)
                              : undefined
                          }
                          onAssign={async (orga, title) => {
                            await onAssign({ id: orga, title: title }, field);
                          }}
                          onRemove={async () => {
                            await onRemove(field);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        </div>

        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <User className="h-4 w-4" />
          <span>{item ? "Person aktualisieren" : "Person erstellen"}</span>
        </Button>
      </form>
    </Form>
  );
};

export default PersonEditor;
